import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { apiRequest } from "../api";
import "../css/multi-user-dashboard.css";
import AddTourTemplateModal from "../components/AddTourTemplateModal";
import DeployTourTemplateModal from "../components/DeployTourTemplateModal";

const TourTemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    const [deletingInstance, setDeletingInstance] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [refreshList, setRefreshList] = useState(false);
    const [showInstancesModal, setShowInstancesModal] = useState(false);
    const [instances, setInstances] = useState([]);
    
    // Filter and sort state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchTemplates();
    }, [refreshList, searchTerm, statusFilter, countryFilter, sortBy, sortOrder]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter !== '') params.append('is_active', statusFilter);
            if (countryFilter) params.append('country', countryFilter);
            if (sortBy) params.append('sort_by', sortBy);
            if (sortOrder) params.append('sort_order', sortOrder);

            const url = `${apiUrl}/tour-templates?${params.toString()}`;
            console.log(`Fetching: ${url}`);
            
            const data = await apiRequest(url, "GET", null, token);
            setTemplates(data);
        } catch (err) {
            setError("Failed to load templates.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setSelectedTemplate(null);
        setShowAddModal(true);
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowAddModal(true);
    };

    const handleDelete = async (templateId) => {
        setDeletingTemplate(templateId);
        try {
            await apiRequest(`${apiUrl}/tour-templates/${templateId}`, "DELETE", null, token);
            setStatusMessage("Template deleted successfully!");
            setRefreshList((prev) => !prev);
        } catch (err) {
            setStatusMessage("Failed to delete template. Please try again.");
        } finally {
            setDeletingTemplate(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    const handleDeploy = (template) => {
        setSelectedTemplate(template);
        setShowDeployModal(true);
    };

    const handleViewInstances = async (template) => {
        setSelectedTemplate(template);
        setShowInstancesModal(true);
        try {
            const data = await apiRequest(`${apiUrl}/tour-templates/${template.id}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setInstances([]);
        }
    };

    const handleDeleteInstance = async (templateId, instanceId) => {
        setDeletingInstance(instanceId);
        try {
            await apiRequest(`${apiUrl}/tour-templates/${templateId}/instances/${instanceId}`, "DELETE", null, token);
            setStatusMessage("Instance deleted successfully!");
            // Refresh the instances list
            const data = await apiRequest(`${apiUrl}/tour-templates/${templateId}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setStatusMessage("Failed to delete instance. Please try again.");
        } finally {
            setDeletingInstance(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="loading">Loading...</div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="error">{error}</div>
                </main>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <main className="main-content">
                <header className="header component">
                    <span className="component-header">Tour Templates</span>
                    <button className="btn btn-primary action" onClick={handleAdd}>
                        <i className="fa fa-plus"></i> New Template
                    </button>
                </header>
                <p><a href="/multi-dashboard">Tour Operations Dashboard</a> &gt; Tour Templates</p>
                <hr />
                {statusMessage && (
                    <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '20px' }}>
                        {statusMessage}
                    </div>
                )}
                
                {/* Filter and Sort Controls */}
                <div className="row mb-3">
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={countryFilter}
                            onChange={(e) => setCountryFilter(e.target.value)}
                        >
                            <option value="">All Countries</option>
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Australia">Australia</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Name</option>
                            <option value="date_from">Start Date</option>
                            <option value="date_to">End Date</option>
                            <option value="country">Country</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </div>
                    <div className="col-md-1">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('');
                                setCountryFilter('');
                                setSortBy('name');
                                setSortOrder('asc');
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                
                <div className="tours">
                    <table>
                        <thead>
                            <tr>
                                <th>NAME</th>
                                <th>DATE FROM</th>
                                <th>DATE TO</th>
                                <th>COUNTRY</th>
                                <th>STATUS</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.length > 0 ? (
                                templates.map((template) => (
                                    <tr key={template.id}>
                                        <td>{template.name}</td>
                                        <td>{template.date_from || "-"}</td>
                                        <td>{template.date_to || "-"}</td>
                                        <td>{template.country || "-"}</td>
                                        <td>{template.is_active ? "Active" : "Inactive"}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-sm btn-view" onClick={() => handleViewInstances(template)}>
                                                    View Instances
                                                </button>
                                                <button className="btn btn-sm btn-primary" onClick={() => handleEdit(template)}>
                                                    Edit
                                                </button>
                                                <button className="btn btn-sm btn-secondary" onClick={() => handleDeploy(template)}>
                                                    Deploy
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-danger" 
                                                    onClick={() => handleDelete(template.id)}
                                                    disabled={deletingTemplate === template.id}
                                                >
                                                    {deletingTemplate === template.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">No templates found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Placeholders for modals */}
                {showAddModal && (
                    <AddTourTemplateModal
                        tourTemplate={selectedTemplate}
                        onClose={() => setShowAddModal(false)}
                        onSave={() => {
                            setShowAddModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showDeployModal && (
                    <DeployTourTemplateModal
                        tourTemplate={selectedTemplate}
                        onClose={() => setShowDeployModal(false)}
                        onDeploy={() => {
                            setShowDeployModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showInstancesModal && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                padding: "20px",
                                borderRadius: "5px",
                                width: "500px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                            }}
                        >
                            <h4>Linked Tour Instances</h4>
                            {statusMessage && (
                                <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '15px' }}>
                                    {statusMessage}
                                </div>
                            )}
                            <ul>
                                {instances.length > 0 ? (
                                    instances.map((inst) => (
                                        <li key={inst.id} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <strong>{inst.university_name}</strong> - {inst.tour_name}
                                                    <br />
                                                    <small style={{ color: '#666' }}>
                                                        Template Controlled: {inst.is_template_controlled ? "Yes" : "No"}
                                                    </small>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteInstance(selectedTemplate.id, inst.id)}
                                                    disabled={deletingInstance === inst.id}
                                                    style={{ marginLeft: '10px' }}
                                                >
                                                    {deletingInstance === inst.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <li>No instances found.</li>
                                )}
                            </ul>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowInstancesModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default TourTemplateManagement; 